import React, {
  useContext, useEffect, useMemo, useRef
} from 'react';
import proptypes from 'prop-types';
const { arrayOf, bool, func, number, shape, string } = proptypes;
import {
  Button,
  Drawer,
  DrawerBody,
  DrawerFooter,
  DrawerHeader,
  ProgressBar,
  Typography
} from '@one-thd/sui-atomic-components';
import { ProductListContext } from '../ProductListContext';
import { PRODUCTS_LOADING_MESSAGE } from '../action-bar/constants';

export const LoadingDrawer = ({
  arrayOfSelectedItems,
  onClose,
  onLoadingSuccess,
  open,
  title
}) => {

  const {
    productsDisplayed,
    loadItemsOnDemand
  } = useContext(ProductListContext);

  // Track if we've already triggered loading for this drawer open
  const hasTriggeredLoadRef = useRef(false);

  const loadingProgress = useMemo(() => {
    const totalProducts = arrayOfSelectedItems?.length || 0;
    if (totalProducts === 0) {
      return 0;
    }

    const matchCount = arrayOfSelectedItems.filter((product) => {
      const productItemId = product?.itemId;
      const productId = product?.id;

      return productsDisplayed?.some((obj) => (productItemId && obj?.itemId === productItemId)
        || (productId && obj?.key === productId)
      );
    }).length;

    return Math.round((matchCount / totalProducts) * 100);
  }, [productsDisplayed, arrayOfSelectedItems]);

  // Reset the load trigger when drawer closes
  useEffect(() => {
    if (!open) {
      hasTriggeredLoadRef.current = false;
    }
  }, [open]);

  // Load unloaded items once when drawer opens
  useEffect(() => {
    if (open && !hasTriggeredLoadRef.current && loadingProgress !== 100) {
      hasTriggeredLoadRef.current = true;
      const itemIds = arrayOfSelectedItems?.map((product) => product.itemId).filter(Boolean) || [];
      if (itemIds.length > 0) {
        loadItemsOnDemand(itemIds);
      }
    }
  }, [open, loadingProgress, arrayOfSelectedItems, loadItemsOnDemand]);

  // Call onLoadingSuccess when loading completes
  useEffect(() => {
    if (open && loadingProgress === 100) {
      onLoadingSuccess();
    }
  }, [loadingProgress, open, onLoadingSuccess]);

  const numberOfItems = arrayOfSelectedItems?.length ?? 0;

  return (
    <Drawer open={open} onClose={onClose} size="sm">
      <DrawerHeader
        title={title}
        onClose={onClose}
      />
      <DrawerBody>
        <div className="sui-min-h-screen">
          <Typography variant="body-lg" weight="bold">Hang Tight!</Typography>
          <Typography variant="body-base">
            {PRODUCTS_LOADING_MESSAGE}
          </Typography>
          <div className="sui-pt-8">
            <ProgressBar
              status={loadingProgress === 100 ? 'success' : undefined}
              value={loadingProgress}
              startLabel={`${numberOfItems} items`}
              endLabel={`${loadingProgress}%`}
            />
          </div>
        </div>
      </DrawerBody>
      <DrawerFooter>
        <Button
          fullWidth
          onClick={onClose}
          variant="secondary"
        >
          Cancel
        </Button>
      </DrawerFooter>
    </Drawer>
  );
};

LoadingDrawer.propTypes = {
  arrayOfSelectedItems: arrayOf(shape(
    {
      itemId: string.isRequired,
      id: string.isRequired,
      quantity: number.isRequired,
    }
  )).isRequired,
  onLoadingSuccess: func,
  onClose: func.isRequired,
  open: bool.isRequired,
  title: string,
};

LoadingDrawer.defaultProps = {
  onLoadingSuccess: () => {},
  title: 'Loading your items',
};